/**
 * Ridalo FAQ — reads the SINGLE source (backend/data/faq/faq.json), the same file that feeds Ask Ridalo and the static
 * SEO page (scripts/seo-static.js). Nothing is copied: edit that file only (see backend/FAQ_SOURCE.md).
 *
 * Also holds the public search: accent-insensitive, with the same synonym groups as Ask Ridalo (FR + EN), so
 * « remise », « discount » and « tarif dégressif » find the same questions.
 */
import source from '../../../backend/data/faq/faq.json';

export const FAQ_SOURCE = source;
export const SURFACES = { faq: 'faq', scooterPatong: 'local:scooter-patong', airportTransfer: 'local:airport-transfer' };

/** Where a link of an entry leads (the router adds the /en prefix on English URLs). */
export const LINK_PATHS = {
  service: { scooters: '/scooters', transfers: '/transport', drivers: '/transport?tab=private_driver', excursions: '/services?category=excursion' },
  page: { terms: '/conditions-generales', contact: '/contact', privacy: '/politique-confidentialite', faq: '/faq' },
};

export const normalizeLang = (lang) => (String(lang || '').toLowerCase().startsWith('en') ? 'en' : 'fr');

export const fold = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const words = (text) => fold(text).match(/[a-z0-9]+/g) || [];

const STOP = new Set(('le la les un une des de du d l et ou en au aux a est sont ce cet cette ces je tu il elle on nous vous ils elles me te se mon ma mes '
  + 'ton ta tes son sa ses notre votre vos leur leurs que qui quoi dont pour par sur sous dans avec sans ne pas plus y quel quelle comment peut peux puis faut '
  + 'the an and or of to in on at is are was were be it this that these those i you he she we they my your his her our their for from by with without not no '
  + 'do does did can could should would will how what which who where when why there').split(' '));

const stem = (t) => {
  let w = t;
  if (w.length > 5 && w.endsWith('ing')) w = w.slice(0, -3);
  if (w.length > 4 && w.endsWith('s')) w = w.slice(0, -1);
  return w.slice(0, 5);
};

const tokens = (text) => words(text).filter((w) => w.length >= 2 && !STOP.has(w)).map(stem);

// One regex per synonym group (member words match by prefix when long, phrases match exactly) — same rule as the backend.
const memberRegex = (member) => {
  const ws = words(member);
  if (!ws.length) return '';
  if (ws.length === 1) {
    const w = ws[0];
    return w.length >= 6 ? `${w.slice(0, Math.max(5, w.length - 2))}[a-z]*` : w;
  }
  return ws.join('\\s+');
};
const GROUPS = (source.synonyms || []).map((g) => {
  const parts = g.map(memberRegex).filter(Boolean);
  return parts.length ? new RegExp(`(?:^|[^a-z0-9])(?:${parts.join('|')})(?![a-z0-9])`) : null;
});
const markers = (text) => {
  const f = fold(text);
  const out = [];
  GROUPS.forEach((rx, i) => { if (rx && rx.test(f)) out.push(`~g${i}`); });
  return out;
};

export const categories = (lang = 'fr') => (source.categories || [])
  .slice().sort((a, b) => a.order - b.order)
  .map((c) => ({ id: c.id, title: c.title[normalizeLang(lang)] || c.title.fr }));

const CAT_TITLES = Object.fromEntries((source.categories || []).map((c) => [c.id, `${c.title.fr} ${c.title.en}`]));

const localize = (e, lang) => ({
  id: e.id, category: e.category, audience: e.audience, scope: e.scope, links: e.links || [],
  q: e.q[lang] || e.q.fr, a: e.a[lang] || e.a.fr, lastVerified: e.last_verified,
  keywords: (e.keywords && (e.keywords[lang] || e.keywords.fr)) || [],
  searchHead: `${e.q.fr} ${e.q.en} ${(e.keywords.fr || []).join(' ')} ${(e.keywords.en || []).join(' ')} ${CAT_TITLES[e.category] || ''}`,
  searchBody: `${e.a.fr} ${e.a.en}`,
});

/** Public entries of a surface (the FAQ page, a local page), in the language asked, in source order. */
export const entriesFor = (surface = SURFACES.faq, lang = 'fr') => (source.entries || [])
  .filter((e) => e.visibility === 'public' && (e.surfaces || []).includes(surface))
  .map((e) => localize(e, normalizeLang(lang)));

/** [{q, a}] list for the local pages' FAQ and their structured data. */
export const localFaq = (surface, lang = 'fr') => entriesFor(surface, lang).map((e) => ({ id: e.id, q: e.q, a: e.a }));

export const lastVerified = () => (source.entries || [])
  .filter((e) => e.visibility === 'public').map((e) => e.last_verified).sort().pop() || source.updated;

const indexed = new WeakMap();
const index = (entry) => {
  if (!indexed.has(entry)) {
    indexed.set(entry, {
      head: new Set(tokens(entry.searchHead)), body: new Set(tokens(entry.searchBody)),
      marks: new Set(markers(`${entry.searchHead} ${entry.searchBody}`)),
    });
  }
  return indexed.get(entry);
};

/**
 * Entries matching `query`, best first. A question matches when its words (or the concepts they belong to) appear in it:
 * question and keywords weigh more than the answer. An empty query returns everything in source order.
 */
export const searchEntries = (entries, query) => {
  const plain = [...new Set(tokens(query))];
  const marks = [...new Set(markers(query))];
  if (!plain.length && !marks.length) return entries.slice();
  const scored = entries.map((entry, position) => {
    const ix = index(entry);
    let score = 0;
    let hits = 0;
    plain.forEach((t) => {
      if (ix.head.has(t)) { score += 3; hits += 1; } else if (ix.body.has(t)) { score += 1; hits += 1; }
    });
    marks.forEach((m) => { if (ix.marks.has(m)) { score += 2; hits += 1; } });
    const needed = Math.max(1, Math.ceil((plain.length + marks.length) / 3));
    return { entry, score, hits, position, ok: hits >= needed };
  });
  return scored.filter((s) => s.ok && s.score > 0)
    .sort((a, b) => b.score - a.score || a.position - b.position).map((s) => s.entry);
};
